import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, FormControlLabel, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useQuery, useMutation } from 'react-fetching-library';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { FormSpy } from 'react-final-form';
import { isUndefined } from 'lodash';
import { RHAP_UI } from 'react-h5-audio-player';
import { toast } from 'react-toastify';

import { Icon, Loader } from 'shared/components';
import { createFetchParticipantData, createUploadParticipantData } from 'api/actions/participant/participant.actions';
import playIcon from 'assets/icons/play.svg';
import { useStyles } from './StudentNameListenerEdit.styles';
import { Footer } from 'shared/templates/main/footer/Footer';
import { AppRoute } from 'app/routing/AppRoute.enum';
import { InputField, Switch } from 'shared/components/controls';
import { useTextToSpeech } from 'app/textToSpeech/useTextToSpeech';
import { useSpeechToText } from 'app/textToSpeech/useSpeechToText';
import { useParticipantQueryData } from 'contexts/participantQueryData/participantQueryDataContext';
import { useUpdateParticipantData, useParticipantData } from 'contexts/participantData/participantDataContext';
import { NameRecordings, ParticipantDataResponse } from 'app/root/Root.types';
import { StudentLoginHeader } from 'app/studentLoginPage/studentLoginHeader/studentLoginHeader';
import { Form } from 'shared/components/form/Form';
// import { StudentClipBuyModalModal } from './studentClipBuyPoll/StudentClipBuyPollModal';
import { StudentNameEditConfirmModal } from './studentNameEditConfirm/StudentNameEditConfirmModal';
import { CreateUploadParticipantDataBody } from 'api/actions/participant/participant.types';
import { AudioRecorderContainer } from './audioRecorder/AudioRecorderContainer';
import { AudioPlayerStyled } from './audioRecorder/AudioRecorder.styles';

export const StudentNameListenerEdit = () => {
  const { formatMessage } = useIntl();

  const [isEditButtonClicked, setEditButtonClicked] = useState(false);
  // const [isEditConfirm, setEditConfirm] = useState(false);
  const [isRecorder, setRecorderClicked] = useState(false);
  const [isAudioUploading, setIsAudioUploading] = useState(false);

  const [isClipBuy, setClipBuy] = useState(false);
  const [nameReadingData, setNameReadingData] = useState<CreateUploadParticipantDataBody>();
  const [recordingStatus, setRecordingStatus] = useState<string>('');
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | undefined>();
  const [recordedTranscript, setRecordedTranscript] = useState<string>();
  const [savedAudioTranscript, setSavedAudioTranscript] = useState<string>();
  const [recordedAIAudio, setRecordedAIAudio] = useState<{ file: Blob; url: string } | null>(null);
  const [audioLanguageId, setAudioLanguageId] = useState<string | undefined>();
  const audioRecorderDeleteHandler = useRef<VoidFunction | null>(null);
  const isDefaultPronunciationIsPlayed = useRef(false);

  const { push } = useHistory();
  const { participantId } = useParticipantQueryData();

  const { mutate, loading: updateParticipantLoading } = useMutation(createUploadParticipantData);

  const isFromInstitution = false;

  const {
    fullName,
    producerLanguageInfo,
    nameRecordings,
    pronunciationName,
    languageId,
    voiceActorId,
    audioPath,
    subtitle,
    secondSubtitle,
    isIncludeSubtitle1,
    isIncludeSubtitle2,
    multilingualActor,
    isMultilingualEvent,
  } = useParticipantData();

  const sanitize = (text?: string | null): string => {
    return text != null ? text.replace(/&/g, 'and').replace(/[<>]/g, '') : '';
  };

  const buildTextToSpeech = (name?: string | null): string => {
    const parts = [sanitize(name)];

    if (isIncludeSubtitle1) {
      parts.push(sanitize(subtitle));
    }

    if (isIncludeSubtitle2) {
      parts.push(sanitize(secondSubtitle));
    }

    return parts
      .map(p => p.trim())
      .filter(Boolean)
      .join(', ');
  };

  const buildTranscribeTextToSpeech = (name?: string | null): string => {
    const parts = [sanitize(name)];

    return parts
      .map(p => p.trim())
      .filter(Boolean)
      .join(', ');
  };

  const textToSpeech = buildTextToSpeech(fullName);

  const textToSpeechPronunciationName = buildTextToSpeech(pronunciationName);

  const [updatePhoneticName, setUpdatePhoneticName] = useState(pronunciationName);

  const updateParticipantData = useUpdateParticipantData();

  const { loading, payload, error } = useQuery<ParticipantDataResponse>(createFetchParticipantData(participantId));

  const nonMulitlingualLanguageOptions = producerLanguageInfo?.map((val: any) => ({
    value: val.languageId,
    label: formatMessage({ id: `virtual_graduation.upload.play_name.language.${val.languageId}` }),
  }));

  const resolvedMultilingualActor = multilingualActor || payload?.multilingualActor;
  const languageOptions = isMultilingualEvent ? resolvedMultilingualActor?.languages : nonMulitlingualLanguageOptions;
  const participantLanguageId = languageId || payload?.languageId;
  const selectedLanguage =
    languageOptions?.find((language: any) => language.id === audioLanguageId) ||
    languageOptions?.find((language: any) => language.id === participantLanguageId) ||
    languageOptions?.find((language: any) => language.id === resolvedMultilingualActor?.defaultLanguage) ||
    null;

  useEffect(() => {
    if (selectedLanguage && audioLanguageId !== selectedLanguage.id) {
      setAudioLanguageId(selectedLanguage.id);
    }
  }, [audioLanguageId, selectedLanguage]);

  const { translateTextToSpeech, playing } = useTextToSpeech();
  const { transcribeAudio, transcribing } = useSpeechToText();

  useEffect(() => {
    if (!loading && !error && !!payload) {
      updateParticipantData(payload);
    }
  }, [error, loading, payload, updateParticipantData]);

  const classes = useStyles();

  useEffect(() => {
    if (!participantId) {
      push(AppRoute.studentLoginPage);
    }
  }, [push, participantId]);

  const play = () => {
    const values = {
      languageId: audioLanguageId || selectedLanguage?.id || payload?.producerLanguageInfo[0]?.languageId || languageId,
      voiceActorId:
        resolvedMultilingualActor?.id || voiceActorId || payload?.producerLanguageInfo[0]?.voiceActors[0]?.id,
      pronunciationName: !pronunciationName ? textToSpeech : textToSpeechPronunciationName,
    };

    translateTextToSpeech({
      textToSpeech: values.pronunciationName,
      language: values.languageId,
      voiceActor: values.voiceActorId,
    });
    isDefaultPronunciationIsPlayed.current = true;
  };

  const getRecordedTranscript = async (blob: Blob) => {
    if (recordedTranscript) {
      return recordedTranscript;
    }

    if (!blob || isAudioUploading) {
      return undefined;
    }

    try {
      const transcript = await transcribeAudio({
        audio: blob,
        language: payload?.producerLanguageInfo[0]?.languageId || languageId || 'en-US',
      });
      const normalizedTranscript = transcript?.trim();

      if (!normalizedTranscript) {
        return undefined;
      }

      setRecordedTranscript(normalizedTranscript);

      return normalizedTranscript;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to transcribe recorded audio', err);

      return undefined;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (value: any) => {
    if (!isDefaultPronunciationIsPlayed.current) {
      toast.error('Please listen to the default pronunciation before confirming.', {
        toastId: participantId,
      });

      return;
    }

    if (isRecorder) {
      await getRecordedTranscript(recordedAudioBlob as Blob);

      setNameReadingData({
        ...value,
        participantId,
        terms: true,
        audioPath: value?.pronunciationAudioURI,
        pronunciationName: recordedTranscript || undefined,
      } as CreateUploadParticipantDataBody);

      await mutate({
        participantId,
        terms: true,
        nameReaderUpVote: true,
        nameRecordings: NameRecordings.SelfRecord,
        audioPath: value?.pronunciationAudioURI,
        pronunciationName: recordedTranscript || undefined,
        languageId: audioLanguageId || payload?.producerLanguageInfo[0]?.languageId,
        voiceActorId: resolvedMultilingualActor?.id,
      });
    }

    if (!isRecorder) {
      const formValue = value || {};
      const submittedPronunciationName = formValue?.pronunciationName?.trim();
      const currentPronunciationName = pronunciationName?.trim();
      const effectivePronunciationName = submittedPronunciationName || currentPronunciationName || fullName;
      const isConfirmedState = effectivePronunciationName === fullName;
      const hadPreviousEditedName = !!currentPronunciationName && currentPronunciationName !== fullName;
      const shouldClearEditedName = isConfirmedState && !hadPreviousEditedName;
      const resolvedPronunciationName = shouldClearEditedName
        ? undefined
        : submittedPronunciationName || currentPronunciationName;
      const resolvedValue = {
        ...formValue,
        pronunciationName: resolvedPronunciationName,
      };

      setNameReadingData({
        ...resolvedValue,
        participantId,
        terms: true,
        nameRecordings: NameRecordings.AudioAi,
      } as CreateUploadParticipantDataBody);

      if (
        !resolvedPronunciationName ||
        resolvedPronunciationName === pronunciationName ||
        textToSpeech === buildTextToSpeech(resolvedPronunciationName)
      ) {
        const state = isConfirmedState ? 'confirmed' : undefined;

        await mutate({
          ...resolvedValue,
          participantId,
          terms: true,
          nameReaderUpVote: true,
          state,
          voiceActorId: resolvedMultilingualActor?.id,
          languageId: audioLanguageId || payload?.producerLanguageInfo[0]?.languageId,
          audioPath: null,
        });
      } else {
        await mutate({
          ...resolvedValue,
          participantId,
          terms: true,
          nameReaderUpVote: false,
          voiceActorId: resolvedMultilingualActor?.id,
          languageId: audioLanguageId || payload?.producerLanguageInfo[0]?.languageId,
          audioPath: null,
        });
      }
    }
    setClipBuy(true);
  };

  const handlePollModalClose = async (nameReaderUpVote?: boolean) => {
    if (isUndefined(nameReaderUpVote)) {
      // setEditConfirm(false);
      setClipBuy(false);

      if (isFromInstitution) {
        push(`${AppRoute.studentLoginByInstitutionPage}?id=${payload?.institution?.id}`);
      } else {
        push(`${AppRoute.studentLoginPage}?id=${payload?.eventId}`);
      }
    }
    setClipBuy(false);

    if (isFromInstitution) {
      push(`${AppRoute.studentLoginByInstitutionPage}?id=${payload?.institution?.id}`);
    } else {
      push(`${AppRoute.studentLoginPage}?id=${payload?.eventId}`);
    }
  };

  const handleOnChange = (checked: boolean) => {
    if (checked) {
      setRecorderClicked(true);
    } else {
      setRecorderClicked(false);
    }
  };

  const participantData = useParticipantData();

  const registerRecorderDeleteHandler = useCallback((handler: VoidFunction) => {
    audioRecorderDeleteHandler.current = handler;
  }, []);

  const clearRecorderState = () => {
    if (recordedAIAudio?.url) {
      URL.revokeObjectURL(recordedAIAudio.url);
    }

    setRecordedAudioBlob(undefined);
    setRecordedTranscript(undefined);
    setRecordedAIAudio(null);
  };

  useEffect(() => {
    setRecordedTranscript(undefined);
  }, [recordedAudioBlob]);

  useEffect(() => {
    setSavedAudioTranscript(undefined);
  }, [audioPath]);

  const handleDelete = async () => {
    updateParticipantData({
      ...participantData,
      audioPath: null,
    });
    await mutate({
      audioPath: null,
      participantId,
      terms: true,
    });
  };

  const handleReadMyVoice = async (blob: Blob) => {
    if (!blob || isAudioUploading) {
      return;
    }
    const transcript = await getRecordedTranscript(blob);

    if (!transcript) {
      return;
    }

    translateTextToSpeech({
      textToSpeech: buildTranscribeTextToSpeech(transcript),
      language: payload?.producerLanguageInfo[0]?.languageId || languageId,
      voiceActor: payload?.producerLanguageInfo[0]?.voiceActors[0]?.id || resolvedMultilingualActor?.id,
      isBlobOnly: true,
      onBlob: (blob: Blob) => {
        const audioURL = URL.createObjectURL(blob);
        setRecordedAIAudio({ file: blob, url: audioURL });
      },
    });
  };

  const getSavedAudioTranscript = async () => {
    if (savedAudioTranscript) {
      return savedAudioTranscript;
    }

    if (!audioPath) {
      return undefined;
    }

    try {
      const response = await fetch(audioPath);

      if (!response.ok) {
        return undefined;
      }

      const audioBlob = await response.blob();
      const transcript = await transcribeAudio({
        audio: audioBlob,
        language: languageId || payload?.producerLanguageInfo[0]?.languageId || 'en-US',
      });
      const normalizedTranscript = transcript?.trim();

      if (!normalizedTranscript) {
        return undefined;
      }

      setSavedAudioTranscript(normalizedTranscript);

      return normalizedTranscript;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to transcribe saved audio', err);

      return undefined;
    }
  };

  const handleReadSavedAIVoice = async () => {
    const transcript = await getSavedAudioTranscript();

    translateTextToSpeech({
      textToSpeech: buildTranscribeTextToSpeech(transcript || pronunciationName || fullName),
      language: payload?.producerLanguageInfo[0]?.languageId || languageId,
      voiceActor: payload?.producerLanguageInfo[0]?.voiceActors[0].id,
    });
  };

  return (
    <>
      <div className={classes.mainContainer}>
        <div>
          <StudentLoginHeader />
        </div>
        <div className={classes.container}>
          <div>
            <Typography className={classes.Title}>{`${fullName}`}</Typography>
            {isRecorder ? (
              <Typography className={classes.subTitle}>
                {formatMessage({
                  id: 'av_user.student_Name_Listener.subTitle',
                })}
              </Typography>
            ) : null}
          </div>
          <div className={classes.cardContainer}>
            {isEditButtonClicked ? (
              <>
                <Card className={classes.card}>
                  {!isRecorder && (
                    <>
                      <div className={classes.phoneticLabel}>
                        <div>
                          <Typography className={classes.phoneticText}>Phonetic Spelling :</Typography>
                        </div>
                        <div className={classes.phoneticName}>
                          <Typography className={classes.editCardInnerParticipantRoleOne}>
                            {updatePhoneticName === undefined || updatePhoneticName === ''
                              ? fullName
                              : updatePhoneticName}
                          </Typography>
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <Form
                      onSubmit={onSubmit}
                      subscription={{}}
                      initialValues={{
                        pronunciationName: pronunciationName || fullName,
                        languageId: languageId || (producerLanguageInfo && producerLanguageInfo[0]?.languageId),
                        voiceActorId: isMultilingualEvent ? voiceActorId : nonMulitlingualLanguageOptions[0]?.value,
                      }}
                      render={({ handleSubmit, form }) => (
                        <FormSpy>
                          {({ values }) => {
                            setUpdatePhoneticName(values.pronunciationName);

                            return (
                              <form onSubmit={handleSubmit}>
                                {isRecorder ? (
                                  <>
                                    <>
                                      <>
                                        <Typography className={classes.voiceRecordingText}>
                                          {formatMessage({
                                            id: 'av_user.student_Name_Listener.voice_recording',
                                          })}
                                        </Typography>
                                        <div className={classes.audioContainer}>
                                          <AudioRecorderContainer
                                            registerDeleteHandler={registerRecorderDeleteHandler}
                                            setIsAudioUploading={setIsAudioUploading}
                                            setRecordingStatus={setRecordingStatus}
                                            onAudioReady={blob => {
                                              if (blob === undefined) {
                                                clearRecorderState();

                                                return;
                                              }
                                              isDefaultPronunciationIsPlayed.current = false;
                                              setRecordedAudioBlob(blob);
                                              handleReadMyVoice(blob);
                                            }}
                                          />
                                        </div>
                                      </>
                                      {recordedAudioBlob && (
                                        <>
                                          <Typography className={classes.voiceRecordingText}>
                                            {formatMessage({
                                              id: 'av_user.student_Name_Listener.listen_and_confirm',
                                            })}
                                          </Typography>
                                          <div className={classes.audioContainer}>
                                            {recordedAIAudio ? (
                                              <AudioPlayerStyled
                                                src={recordedAIAudio.url}
                                                showJumpControls={false}
                                                customControlsSection={[RHAP_UI.MAIN_CONTROLS]}
                                                onPlay={() => {
                                                  isDefaultPronunciationIsPlayed.current = true;
                                                }}
                                              />
                                            ) : (
                                              <div className={classes.audioLoaderContainer}>
                                                <Loader />
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </>
                                    {recordedAudioBlob && (
                                      <div className={classes.audioRecorderConfirmButton}>
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          className={classes.reRecordButton}
                                          size="small"
                                          onClick={() => {
                                            if (audioRecorderDeleteHandler.current) {
                                              audioRecorderDeleteHandler.current();
                                            } else {
                                              clearRecorderState();
                                            }
                                          }}
                                        >
                                          {formatMessage({
                                            id: 'av_user.student_Name_Listener.re_record',
                                          })}
                                        </Button>
                                        <Button
                                          type="submit"
                                          variant="outlined"
                                          size="small"
                                          className={classes.editConfirmButton}
                                          disabled={
                                            recordingStatus === 'recording' ||
                                            recordingStatus === 'stopping' ||
                                            isAudioUploading ||
                                            transcribing ||
                                            recordedAIAudio === null
                                          }
                                        >
                                          <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />
                                          {formatMessage({
                                            id: 'av_user.student_Name_Listener.button_confirm',
                                          })}
                                        </Button>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className={classes.formControl}>
                                      <Typography className={classes.labelTop}>
                                        {formatMessage({
                                          id: 'av_user.student_Name_Listener.edit_name_label',
                                        })}
                                      </Typography>

                                      <InputField
                                        name="pronunciationName"
                                        variant="outlined"
                                        defaultValue={pronunciationName || fullName}
                                        className={classes.editCardInnerParticipantRole}
                                        autoFocus={isEditButtonClicked}
                                        onChange={() => {
                                          isDefaultPronunciationIsPlayed.current = false;
                                        }}
                                      />
                                    </div>

                                    {languageOptions?.length > 0 && (
                                      <div className={classes.formControl}>
                                        <Typography className={classes.labelTop}>Language</Typography>
                                        <Autocomplete
                                          className={classes.languageAutocomplete}
                                          classes={{
                                            paper: classes.languageDropdownPaper,
                                            option: classes.languageDropdownOption,
                                          }}
                                          options={languageOptions}
                                          getOptionLabel={option => option.label}
                                          getOptionSelected={(option, value) => option.id === value.id}
                                          value={selectedLanguage}
                                          onChange={(_, option) => {
                                            if (option) {
                                              setAudioLanguageId(option.id);
                                              form.change('languageId', option.id);
                                            }
                                          }}
                                          renderInput={params => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              fullWidth
                                              inputProps={{ ...params.inputProps, 'aria-label': 'Language' }}
                                            />
                                          )}
                                          disableClearable
                                          autoHighlight
                                        />
                                      </div>
                                    )}
                                    <div className={classes.dividerLine} />
                                    <div className={classes.editButtonContainer}>
                                      <Button
                                        disabled={playing}
                                        type="button"
                                        variant="outlined"
                                        onClick={() => {
                                          isDefaultPronunciationIsPlayed.current = true;
                                          translateTextToSpeech({
                                            textToSpeech: buildTextToSpeech(values.pronunciationName),
                                            language: audioLanguageId || payload?.producerLanguageInfo[0]?.languageId,
                                            voiceActor:
                                              multilingualActor?.id ||
                                              payload?.producerLanguageInfo[0]?.voiceActors[0]?.id,
                                          });
                                        }}
                                        className={classes.editlistenButton}
                                      >
                                        <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />

                                        {formatMessage({
                                          id: 'av_user.student_Name_Listener.button_listen',
                                        })}
                                      </Button>
                                      <Button type="submit" variant="outlined" className={classes.editConfirmButton}>
                                        <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />
                                        {formatMessage({
                                          id: 'av_user.student_Name_Listener.button_confirm',
                                        })}
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </form>
                            );
                          }}
                        </FormSpy>
                      )}
                    />
                  </div>
                </Card>
                <div className={classes.switchWithLabel}>
                  <FormControlLabel
                    control={
                      <Switch
                        classes={{
                          thumb: classes.switchThumb,
                        }}
                        onChange={handleOnChange}
                      />
                    }
                    label="Can't get it right? Record your name instead."
                  />
                </div>
              </>
            ) : (
              <Card className={classes.card}>
                <>
                  <div className={classes.buttonMainContainer}>
                    {!audioPath && (
                      <div className={classes.phoneticLabel}>
                        <div>
                          <Typography className={classes.editCardInnerParticipantRoleOne}>
                            Phonetic Spelling :
                          </Typography>
                        </div>
                        <div className={classes.phoneticName}>
                          <Typography className={classes.editCardInnerParticipantRoleOne}>
                            {pronunciationName === undefined || pronunciationName === '' || pronunciationName === null
                              ? fullName
                              : pronunciationName}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={classes.listenButtonContainer}>
                    {/* {payload.pr} */}
                    {audioPath ? (
                      <>
                        <AudioPlayerStyled
                          src={audioPath || ''}
                          showJumpControls={false}
                          customAdditionalControls={[RHAP_UI.MAIN_CONTROLS]}
                          customVolumeControls={[
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              onClick={handleDelete}
                              type="submit"
                              // disabled={disabled}
                            >
                              <Icon icon="Delete" /> Delete
                            </Button>,
                          ]}
                          customControlsSection={[
                            RHAP_UI.MAIN_CONTROLS,
                            <div className="audio-player-actions">
                              {nameRecordings === NameRecordings.SelfRecord && (
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="small"
                                  className={classes.readMyVoiceButton}
                                  disabled={transcribing || isAudioUploading}
                                  onClick={handleReadSavedAIVoice}
                                >
                                  <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />
                                  {formatMessage({
                                    id: 'av_user.student_Name_Listener.button_read_my_ai_voice',
                                    defaultMessage: 'AI voice',
                                  })}
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                type="submit"
                                onClick={handleDelete}
                              >
                                <Icon icon="Delete" /> Delete
                              </Button>
                            </div>,
                          ]}
                        />
                      </>
                    ) : (
                      <div className={classes.languageSelectorListenRow}>
                        {languageOptions?.length > 0 && (
                          <Autocomplete
                            className={classes.languageAutocomplete}
                            classes={{
                              paper: classes.languageDropdownPaper,
                              option: classes.languageDropdownOption,
                            }}
                            options={languageOptions}
                            getOptionLabel={option => option.label}
                            getOptionSelected={(option, value) => option.id === value.id}
                            value={selectedLanguage}
                            onChange={(_, option) => option && setAudioLanguageId(option.id)}
                            renderInput={params => (
                              <TextField
                                {...params}
                                variant="outlined"
                                fullWidth
                                inputProps={{ ...params.inputProps, 'aria-label': 'Language' }}
                              />
                            )}
                            disableClearable
                            autoHighlight
                          />
                        )}
                        <Button
                          disabled={playing}
                          type="button"
                          variant="outlined"
                          onClick={play}
                          className={classes.listenButton}
                        >
                          <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />
                          {formatMessage({
                            id: 'av_user.student_Name_Listener.button_listen',
                          })}
                        </Button>
                      </div>
                    )}
                    {/* <Button
                      disabled={playing}
                      type="button"
                      variant="outlined"
                      onClick={play}
                      className={classes.listenButton}
                    >
                      <img src={playIcon} alt="icon" className={classes.listenButtonIcon} />
                      {formatMessage({ id: 'av_user.student_Name_Listener.button_listen' })}
                    </Button> */}
                    <Button
                      type="button"
                      variant="outlined"
                      className={classes.editButton}
                      onClick={() => setEditButtonClicked(true)}
                    >
                      {formatMessage({
                        id: 'av_user.student_Name_Listener.button_Edit_Name',
                      })}
                    </Button>
                  </div>
                </>
              </Card>
            )}
          </div>
          {!isEditButtonClicked && !audioPath && (
            <div className={classes.buttonContainer}>
              <Button
                type="button"
                variant="text"
                className={classes.confirmButton}
                // onClick={() => setEditConfirm(true)}
                onClick={() => {
                  onSubmit(nameReadingData);
                }}
                disabled={updateParticipantLoading}
              >
                {formatMessage({
                  id: 'av_user.student_Name_Listener.button_confirm',
                })}
              </Button>
            </div>
          )}

          {/* <div className={classes.infoContainer}>
            <Card className={classes.infoTextCard}>
              <Typography className={classes.cardInnerInfoText}>
                {formatMessage({ id: 'av_user.student_Name_Listener.info_text' })}
              </Typography>
              <Typography className={classes.cardInnerInfoText}>
                {formatMessage({ id: 'av_user.student_Name_Listener.info_text_2' })}
              </Typography>
            </Card>
          </div> */}
          {/* <Typography className={classes.clientText}>
            {formatMessage({ id: 'av_user.student_Name_Listener.client_Text' })}
          </Typography>
          <div className={classes.imageContainer}>
            <img src={img4} alt="HeaderImage" className={classes.images} />
            <img src={img3} alt="HeaderImage" className={classes.images} />
            <img src={img1} alt="HeaderImage" className={classes.images} />
          </div> */}
        </div>
        <div className={classes.footerContainer}>
          <Footer />
        </div>
      </div>
      {/* {isEditConfirm && <StudentClipBuyModalModal open={isEditConfirm} handleClose={handlePollModalClose} />} */}
      {isClipBuy && (
        <StudentNameEditConfirmModal
          open={isClipBuy}
          handleClose={handlePollModalClose}
          updateParticipantLoading={updateParticipantLoading}
        />
      )}
    </>
  );
};
